/**
 * Activity-event endpoints: per-project and per-session feeds, analytics
 * aggregates, and the user-scoped summary.
 *
 * Nothing here caches — TanStack Query owns response caching and
 * invalidation at the hook layer. Reporting reads always carry exact bounds.
 */

import type { ActivityEvent, ActivitySummary } from '@/types/activity'
import type {
  ProjectAgentCountResponse,
  ProjectAnalytics,
} from '@/types/project'
import type {
  ProjectSessionsGroupedResponse,
  SessionAnalytics,
  SessionInfo,
} from '@/types/session'
import type {
  SessionDetailBundle,
  SessionTimelineEvent,
} from '@/types/sessionDetail'
import type { CliSystem } from '@/types/tokens'
import { apiClient } from './client'

export const activities = {
  getProjectActivities: async (
    projectId: string,
    params?: {
      session_id?: string
      event_types?: string
      status_filter?: string
      cli_system?: string
      start_time?: string
      end_time?: string
      limit?: number
      offset?: number
    },
    signal?: AbortSignal
  ): Promise<ActivityEvent[]> => {
    const response = await apiClient.get<ActivityEvent[]>(
      `/api/v1/activities/project/${projectId}`,
      { params, signal }
    )
    return response.data
  },

  getProjectSessions: async (
    projectId: string,
    params?: {
      start_time?: string
      end_time?: string
      cli_system?: CliSystem
    }
  ): Promise<SessionInfo[]> => {
    const response = await apiClient.get<SessionInfo[]>(
      `/api/v1/activities/project/${projectId}/sessions`,
      { params }
    )
    return response.data
  },

  getProjectSessionsGrouped: async (
    projectId: string,
    params?: {
      start_time?: string
      end_time?: string
    }
  ): Promise<ProjectSessionsGroupedResponse> => {
    const response = await apiClient.get<ProjectSessionsGroupedResponse>(
      `/api/v1/activities/project/${projectId}/sessions/grouped`,
      { params }
    )
    return response.data
  },

  getProjectAnalytics: async (
    projectId: string,
    params: { start_time: string; end_time: string },
    signal?: AbortSignal
  ): Promise<ProjectAnalytics> => {
    const response = await apiClient.get<ProjectAnalytics>(
      `/api/v1/activities/analytics/project/${projectId}`,
      { params, signal }
    )
    return response.data
  },

  getProjectAgentCount: async (
    projectId: string,
    hours: number = 24
  ): Promise<ProjectAgentCountResponse> => {
    const response = await apiClient.get<ProjectAgentCountResponse>(
      `/api/v1/activities/project/${projectId}/agent-count`,
      { params: { hours } }
    )
    return response.data
  },

  getSessionAnalytics: async (
    sessionId: string,
    params?: {
      start_time?: string
      end_time?: string
    }
  ): Promise<SessionAnalytics> => {
    const response = await apiClient.get<SessionAnalytics>(
      `/api/v1/activities/session/${sessionId}/analytics`,
      { params }
    )
    return response.data
  },

  getSessionActivities: async (
    sessionId: string,
    params?: {
      limit?: number
      offset?: number
    },
    signal?: AbortSignal
  ): Promise<ActivityEvent[]> => {
    const response = await apiClient.get<ActivityEvent[]>(
      `/api/v1/activities/session/${sessionId}`,
      { params, signal }
    )
    return response.data
  },

  /**
   * Get most recent activity events across all projects.
   *
   * Provides global view of recent activity for the default landing page
   * without requiring project/session selection.
   *
   * @param params.limit - Maximum events to return (default: 50, max: 100)
   * @param params.event_types - Comma-separated event types to filter
   * @param params.status_filter - Filter by status (success/failure/warning)
   * @returns Array of recent activity events
   *
   * @example
   * // Get 50 most recent events
   * const events = await api.activities.getRecentEvents({ limit: 50 })
   *
   * // Filter to failures only
   * const failures = await api.activities.getRecentEvents({
   *   limit: 50,
   *   status_filter: 'failure'
   * })
   */
  getRecentEvents: async (params?: {
    limit?: number
    event_types?: string
    status_filter?: string
  }): Promise<ActivityEvent[]> => {
    const response = await apiClient.get<ActivityEvent[]>(
      '/api/v1/activities/recent',
      { params }
    )
    return response.data
  },

  // Slim rows can still be sizeable on very long sessions, so the call
  // carries its own timeout above the client's 30s default, and the signal
  // lets navigation cancel an in-flight download.
  getSessionDetail: async (
    sessionId: string,
    opts: { signal?: AbortSignal } = {}
  ): Promise<SessionDetailBundle> => {
    const response = await apiClient.get<SessionDetailBundle>(
      `/api/v1/activities/session/${sessionId}/detail`,
      { timeout: 120_000, signal: opts.signal }
    )
    return response.data
  },

  // The expansion read behind the bundle's slim timeline rows: one event's
  // full (per-field capped) details/context, fetched when a row opens.
  getSessionEventDetail: async (
    sessionId: string,
    eventId: number,
    opts: { signal?: AbortSignal } = {}
  ): Promise<SessionTimelineEvent> => {
    const response = await apiClient.get<SessionTimelineEvent>(
      `/api/v1/activities/session/${sessionId}/events/${eventId}/detail`,
      { signal: opts.signal }
    )
    return response.data
  },

  /**
   * User-scoped activity summary for the sidebar's at-a-glance stats.
   * Backed by the re-homed aggregate at GET /api/v1/activities/summary/
   * (requires an authenticated browser/Entra session — not available to
   * unauthenticated or machine-principal callers).
   *
   * Returns total_events / active_sessions / error_rate used by the sidebar,
   * plus type/status distributions and an hourly series.
   */
  getActivitySummary: async (params?: {
    hours?: number
  }): Promise<ActivitySummary> => {
    const response = await apiClient.get<ActivitySummary>(
      '/api/v1/activities/summary/',
      { params }
    )
    return response.data
  },
}
